<template>
  <AnalyticsCard
    title="When you get booked"
    :subtitle="`Meetings by weekday and hour, in ${zoneLabel}`"
    :loading="loading"
    :empty="isEmpty"
    empty-title="No meetings scheduled in this period"
    empty-hint="This grid shows the hours people actually pick, which is the argument for opening or closing availability."
    :height="320"
  >
    <template #actions>
      <div class="hidden sm:flex items-center gap-1.5">
        <span class="text-[11px] text-gray-400 dark:text-gray-500">Fewer</span>
        <span v-for="step in 5" :key="step" class="w-3.5 h-3.5 rounded-[3px]"
          :style="{ backgroundColor: shade(step / 5) }"></span>
        <span class="text-[11px] text-gray-400 dark:text-gray-500">More</span>
      </div>
    </template>

    <div class="p-4 overflow-x-auto">
      <div class="min-w-[560px]">
        <!-- Hour ruler. Only every third hour is labelled: 24 labels at this
             width overlap into noise. -->
        <!-- pl-11 clears the weekday label (w-9) plus its gap; pr-9 clears the
             row-total column (w-7) plus its gap, so the ruler tracks the cells
             rather than drifting a column right of them. -->
        <div class="grid gap-[3px] mb-1.5 pl-11 pr-9" :style="gridStyle">
          <span v-for="h in 24" :key="`ruler-${h}`"
            class="text-[9px] text-gray-400 dark:text-gray-500 text-center tabular-nums leading-none">
            {{ (h - 1) % 3 === 0 ? hourLabel(h - 1) : '' }}
          </span>
        </div>

        <div v-for="(day, dow) in DAYS" :key="day" class="flex items-center gap-2 mb-[3px]">
          <span class="w-9 text-[11px] font-medium text-gray-500 dark:text-gray-400 text-right flex-shrink-0">{{ day }}</span>
          <div class="grid gap-[3px] flex-1" :style="gridStyle">
            <div v-for="hour in 24" :key="`${dow}-${hour}`"
              class="aspect-square rounded-[3px] transition-colors"
              :style="{ backgroundColor: shade(intensity(dow, hour - 1)) }"
              :title="cellTitle(dow, hour - 1)"></div>
          </div>
          <span class="w-7 text-[11px] font-semibold text-gray-700 dark:text-gray-200 tabular-nums text-right flex-shrink-0">
            {{ dayTotals[dow] || '' }}
          </span>
        </div>
      </div>
    </div>
  </AnalyticsCard>
</template>

<script setup>
import { computed } from 'vue'
import AnalyticsCard from '../shared/AnalyticsCard.vue'
import { PALETTE, hexToRgba } from '../shared/useAnalyticsChart.js'

const props = defineProps({
  // [[dayOfWeek, hour, count], …] — sparse, only non-zero cells.
  cells:   { type: Array, default: () => [] },
  zone:    { type: String, default: '' },
  loading: { type: Boolean, default: false },
})

// A GitHub-style grid rather than a Highcharts heatmap: the heatmap module is
// not among the ones loaded in layouts/_highcharts.html.erb, and 168 divs
// render crisper at this size than an SVG raster would, in both themes.
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const gridStyle = { gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }

const lookup = computed(() => {
  const map = new Map()
  props.cells.forEach(([dow, hour, count]) => map.set(`${dow}-${hour}`, count))
  return map
})

const max = computed(() => Math.max(1, ...props.cells.map(c => c[2])))
const isEmpty = computed(() => !props.loading && props.cells.length === 0)

const dayTotals = computed(() => {
  const totals = Array(7).fill(0)
  props.cells.forEach(([dow, , count]) => { totals[dow] += count })
  return totals
})

const zoneLabel = computed(() => (props.zone || 'your timezone').replace(/_/g, ' '))

function countAt(dow, hour) {
  return lookup.value.get(`${dow}-${hour}`) || 0
}

// Square-rooted so a single busy hour does not flatten every other cell to the
// empty shade. Linear scaling makes a grid with one outlier look like an empty
// grid, which is exactly the case where the shape matters most.
function intensity(dow, hour) {
  const count = countAt(dow, hour)
  return count === 0 ? 0 : Math.sqrt(count / max.value)
}

function shade(value) {
  if (!value) return 'rgba(148, 163, 184, 0.12)'
  return hexToRgba(PALETTE.blue, 0.15 + value * 0.85)
}

function hourLabel(hour) {
  if (hour === 0) return '12a'
  if (hour === 12) return '12p'
  return hour < 12 ? `${hour}a` : `${hour - 12}p`
}

function cellTitle(dow, hour) {
  const count = countAt(dow, hour)
  const when = `${DAYS[dow]} ${hourLabel(hour)}`
  return count === 1 ? `${when}: 1 meeting` : `${when}: ${count} meetings`
}
</script>
